/**
 * Portada desde el backoffice: banners y destacados (ADM-05, ADM-06).
 *
 * Esta aparte de `administracion.servicio.ts` porque es otra pantalla y otro
 * recurso; ese archivo ya carga el panel, los pedidos y las busquedas.
 */
import type {
  ActualizarBannerDto,
  ActualizarConfiguracionPortadaDto,
  ActualizarSeoDto,
  Banner,
  CategoriaAdminResumen,
  ConfiguracionPortada,
  CrearBannerDto,
  ImagenSubidaDto,
  MoverBannerDto,
  Paginado,
  ProductoAdminResumen,
} from '@elcarpintero/compartido';

import { pedir } from './cliente-api';

/** Todos, activos y apagados: el backoffice administra los dos. */
export function obtenerBanners(senal?: AbortSignal): Promise<Banner[]> {
  return pedir<Banner[]>('/admin/banners', { ...(senal ? { senal } : {}) });
}

/**
 * Sube la pieza de un banner y devuelve su clave, SIN crear el banner (ADM-04).
 *
 * Son dos pasos porque un banner no es solo la imagen: el formulario sube
 * primero para poder mostrarla y saber cuanto mide, y manda la clave junto con
 * el resto de los campos al guardar.
 */
export function subirImagenBanner(archivo: File): Promise<ImagenSubidaDto> {
  return pedir<ImagenSubidaDto>('/admin/banners/imagen', {
    metodo: 'POST',
    cuerpoBinario: archivo,
  });
}

export function crearBanner(datos: CrearBannerDto): Promise<Banner> {
  return pedir<Banner>('/admin/banners', { metodo: 'POST', cuerpo: datos });
}

export function actualizarBanner(id: number, datos: ActualizarBannerDto): Promise<Banner> {
  return pedir<Banner>(`/admin/banners/${id}`, { metodo: 'PATCH', cuerpo: datos });
}

export function eliminarBanner(id: number): Promise<void> {
  return pedir<void>(`/admin/banners/${id}`, { metodo: 'DELETE' });
}

/** Devuelve la lista completa: mover cambia la posicion de dos banners. */
export function moverBanner(id: number, direccion: MoverBannerDto['direccion']): Promise<Banner[]> {
  return pedir<Banner[]>(`/admin/banners/${id}/mover`, {
    metodo: 'POST',
    cuerpo: { direccion } satisfies MoverBannerDto,
  });
}

export function obtenerCategoriasAdmin(senal?: AbortSignal): Promise<CategoriaAdminResumen[]> {
  return pedir<CategoriaAdminResumen[]>('/admin/categorias', { ...(senal ? { senal } : {}) });
}

export function destacarCategoria(id: number, destacada: boolean): Promise<void> {
  return pedir<void>(`/admin/categorias/${id}/destacada`, {
    metodo: 'PATCH',
    cuerpo: { destacada },
  });
}

/** Titulo y descripcion SEO de una categoria (ADM-08). */
export function actualizarSeoCategoria(id: number, datos: ActualizarSeoDto): Promise<void> {
  return pedir<void>(`/admin/categorias/${id}/seo`, { metodo: 'PATCH', cuerpo: datos });
}

export function destacarProducto(id: number, destacado: boolean): Promise<void> {
  return pedir<void>(`/admin/productos/${id}/destacado`, {
    metodo: 'PATCH',
    cuerpo: { destacado },
  });
}

/** Cuantos destacados muestra la portada y hasta cuanto se puede subir (ADM-06). */
export function obtenerConfiguracionPortada(senal?: AbortSignal): Promise<ConfiguracionPortada> {
  return pedir<ConfiguracionPortada>('/admin/portada/configuracion', {
    ...(senal ? { senal } : {}),
  });
}

export function guardarMaximoDestacados(maximoDestacados: number): Promise<ConfiguracionPortada> {
  return pedir<ConfiguracionPortada>('/admin/portada/configuracion', {
    metodo: 'PUT',
    cuerpo: { maximoDestacados } satisfies ActualizarConfiguracionPortadaDto,
  });
}

/**
 * Reemplaza la foto de la banda "quienes somos" de la portada. El servidor la
 * convierte y la mide; devuelve la configuracion con la foto nueva.
 */
export function subirFotoTienda(archivo: File): Promise<ConfiguracionPortada> {
  return pedir<ConfiguracionPortada>('/admin/portada/foto-tienda', {
    metodo: 'PUT',
    cuerpoBinario: archivo,
  });
}

/** Vuelve a la foto de respaldo (la fachada de San Diego). */
export function quitarFotoTienda(): Promise<ConfiguracionPortada> {
  return pedir<ConfiguracionPortada>('/admin/portada/foto-tienda', { metodo: 'DELETE' });
}

/** Lo mas que entrega la tabla de productos por pagina (`POR_PAGINA_MAXIMO`). */
const POR_PAGINA_DESTACADOS = 96;

/**
 * Todos los marcados, incluso los que sobran del maximo. Reusa la tabla de
 * productos con su filtro; como el maximo llega a cien y la pagina a 96, pide
 * las paginas que haga falta.
 */
export async function obtenerProductosDestacados(
  senal?: AbortSignal,
): Promise<ProductoAdminResumen[]> {
  const productos: ProductoAdminResumen[] = [];

  for (let pagina = 1; ; pagina += 1) {
    const respuesta = await pedir<Paginado<ProductoAdminResumen>>('/admin/productos', {
      parametros: { soloDestacados: true, porPagina: POR_PAGINA_DESTACADOS, pagina },
      ...(senal ? { senal } : {}),
    });
    productos.push(...respuesta.items);
    if (respuesta.items.length === 0 || productos.length >= respuesta.total) return productos;
  }
}
